import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from 'styled-components';
import { onAuthStateChanged } from 'firebase/auth';
import { getDoc, doc } from 'firebase/firestore';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';


import GlobalStyles from './styles/GlobalStyles';
import theme from './styles/theme';
import { auth, db } from './firebase/config';
import { AuthProvider } from './contexts/AuthContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { MessageProvider } from './contexts/MessageContext';
import ProtectedRoute from './components/ProtectedRoute';
import Layout from './components/layout/Layout';

// Import pages
import Home from './pages/Home';
import Login from './pages/auth/Login';
import SignUp from './pages/auth/SignUp';
import PartnerSignUp from './pages/auth/PartnerSignUp';
import ForgotPassword from './pages/auth/ForgotPassword';
import Journal from './pages/Journal';
import JournalArticle from './pages/JournalArticle';
import OurStory from './pages/OurStory';
import Contact from './pages/Contact';
import AllPlans from './pages/AllPlans';
import Nutritionists from './pages/Nutritionists';
import OurExperts from './pages/OurExperts';
import BecomeNutritionist from './pages/BecomeNutritionist';

// Admin pages
import AdminDashboard from './pages/admin/AdminDashboard';
import CustomerManagement from './pages/admin/CustomerManagement';
import PartnerManagement from './pages/admin/PartnerManagement';
import PartnerDetail from './pages/admin/PartnerDetail';
import ContentManagement from './pages/admin/ContentManagement';
import AdminPlanManagement from './pages/admin/AdminPlanManagement';
import NotificationManagement from './pages/admin/NotificationManagement';
import NutritionistManagement from './pages/admin/NutritionistManagement';

// Partner pages
import PartnerDashboard from './pages/partner/PartnerDashboard';
import ClientManagement from './pages/partner/ClientManagement';
import PartnerConsultations from './pages/partner/PartnerConsultations';
import PlanManagement from './pages/partner/PlanManagement';
import PartnerProfile from './pages/partner/PartnerProfile';
import ChatWithClients from './pages/partner/ChatWithClients';
import ClientDetailsView from './pages/partner/ClientDetailsView';
import PartnerSupport from './pages/partner/PartnerSupport';
import VideosManagement from './pages/partner/VideosManagement';
import PartnerAppointment from './pages/partner/PartnerAppointment';

// Customer pages
import CustomerDashboard from './pages/customer/CustomerDashboard';
import ChatWithPartner from './pages/customer/ChatWithPartner';
import CustomerProfile from './pages/customer/CustomerProfile';
import MyDailyPlans from './pages/customer/MyDailyPlans'
import CustomerSupport from './pages/customer/CustomerSupport';
import ClientVideos from './pages/client/ClientVideos';
import SelectPlanPage from './pages/customer/SelectPlanPage.';
import PlanDetailsPage from './pages/customer/PlanDetailsPage';
import ClientAppointment from './pages/customer/ClientAppointment';

// Dashboard pages
// import AskACoach from './pages/dashboard/AskACoach';

// Layouts
import AdminLayout from './layouts/AdminLayout';
import PartnerLayout from './layouts/PartnerLayout';
import CustomerLayout from './layouts/CustomerLayout';
import DailyProgressTracker from './pages/customer/DailyProgressTracker';
import ProgressDashboard from './pages/customer/ProgressDashboard';
import { useAuth } from './contexts/AuthContext';
import {  Outlet } from 'react-router-dom';
const PlanAccessGuard = () => {
  const { currentUser } = useAuth();
  const [isAllowed, setIsAllowed] = useState(null); // null = loading, true = allowed, false = redirect

  useEffect(() => {
    const checkPlanStatus = async () => {
      if (!currentUser) return setIsAllowed(false);

      try {
        const userRef = doc(db, 'users', currentUser.uid);
        const userSnap = await getDoc(userRef);
        const userData = userSnap.data();
        const selectedPlanId = userData?.selectedPlanId;

        if (!selectedPlanId) return setIsAllowed(false); // no plan at all

        const progressRef = doc(db, 'users', currentUser.uid, 'planProgress', 'current');
        const progressSnap = await getDoc(progressRef);

        if (!progressSnap.exists()) return setIsAllowed(false);

        const progressData = progressSnap.data();
        const isExpired = progressData.progressPercentage >= 100;

        setIsAllowed(!isExpired);
      } catch (err) {
        console.error('Error checking plan status:', err);
        setIsAllowed(false);
      }
    };

    checkPlanStatus();
  }, [currentUser]);

  if (isAllowed === null) return <div>Checking access...</div>;

  return isAllowed ? <Outlet /> : <Navigate to="/customer" replace />;
};
// Customer Routes Component
const CustomerRoutes = () => {
  return (
    <CustomerLayout>
      <Routes>
        <Route path="/" element={<CustomerDashboard />} />
        <Route path="/profile" element={<CustomerProfile />} />
         <Route path="/select-plan" element={<SelectPlanPage />} />
<Route path="/select-plan/:id" element={<PlanDetailsPage />} />
<Route path="/consultations" element={<ClientAppointment/>} />
        <Route element={<PlanAccessGuard />}>
        <Route path="/meal-plans" element={<MyDailyPlans />} />
        <Route path="/consultations" element={<ClientAppointment/>} />
        <Route path="/chat" element={<ChatWithPartner />} />
        <Route path="/daily" element={<DailyProgressTracker />} />
        <Route path="/progress" element={<ProgressDashboard />} />
        <Route path="/support" element={<CustomerSupport />} />
        <Route path="/videos" element={<ClientVideos />} />
        </Route>
        <Route path="*" element={<Navigate to="/customer" replace />} />
      </Routes>
    </CustomerLayout>
  );
};

// Partner Routes Component
const PartnerRoutes = () => {
  return (
    <PartnerLayout>
      <Routes>
        <Route path="/" element={<PartnerDashboard />} />
        <Route path="/clients" element={<ClientManagement />} />
        <Route path="/plans" element={<PlanManagement />} />
        <Route path="/consultations" element={<PartnerAppointment />} />
        <Route path="/profile" element={<PartnerProfile />} />
        <Route path="/chat-with-clients" element={<ChatWithClients />} />
        <Route path="/messages" element={<ChatWithClients />} />
        <Route path="/support" element={<PartnerSupport />} />
        <Route path="/clients/:clientId" element={<ClientDetailsView />} />
        <Route path="/videos" element={<VideosManagement />} />
        <Route path="*" element={<Navigate to="/partner" replace />} />
      </Routes>
    </PartnerLayout>
  );
};

// Admin Routes Component
const AdminRoutes = () => {
  return (
    <AdminLayout>
      <Routes>
        <Route path="/" element={<AdminDashboard />} />
        <Route path="/customers" element={<CustomerManagement />} />
        <Route path="/partners" element={<PartnerManagement />} />
        <Route path="/partners/:partnerId" element={<PartnerDetail />} />
        <Route path="/content" element={<ContentManagement />} />
        <Route path="/plan-manage" element={<AdminPlanManagement />} />
        <Route path="/notifications" element={<NotificationManagement />} />
        <Route path="/nutritionists" element={<NutritionistManagement />} />
        <Route path="*" element={<Navigate to="/admin" replace />} />
      </Routes>
    </AdminLayout>
  );
};

function App() {
  const [currentUser, setCurrentUser] = useState(null);
  const [initializing, setInitializing] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        try {
          // Get the user role from Firestore
          let role = 'customer'; // Default role
          
          // Check admin collection first
          const adminDoc = await getDoc(doc(db, 'admins', user.uid));
          if (adminDoc.exists()) {
            role = 'admin';
          } else {
            // Then check partner collection
            const partnerDoc = await getDoc(doc(db, 'partners', user.uid));
            if (partnerDoc.exists()) {
              role = 'partner';
            } else {
              // Finally check users collection
              const userDoc = await getDoc(doc(db, 'users', user.uid));
              if (userDoc.exists() && userDoc.data()?.role) {
                role = userDoc.data().role;
              }
            }
          }
          
          setCurrentUser({ 
            ...user, 
            role
          });
        } catch (error) {
          console.error('Error getting user data:', error);
          setCurrentUser({
            ...user,
            role: 'customer' // Default to customer on error
          });
        }
      } else {
        setCurrentUser(null);
      }
      setInitializing(false);
    });

    return () => unsubscribe();
  }, []);

  if (initializing) {
    return (
      <ThemeProvider theme={theme}>
        <GlobalStyles />
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
          Loading...
        </div>
      </ThemeProvider>
    );
  }

  return (
    <AuthProvider>
      <ThemeProvider theme={theme}>
        <GlobalStyles />
        <ToastContainer position="top-right" />
        <Router>
          <MessageProvider>
            <NotificationProvider>
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/journal" element={<Layout><Journal /></Layout>} />
                <Route path="/journal/:id" element={<Layout><JournalArticle /></Layout>} />
                <Route path="/OurStory" element={<Layout><OurStory /></Layout>} />
                <Route path="/contact" element={<Layout><Contact /></Layout>} />
                
                {/* Shop routes */}
                <Route path="/shop/workout-plans" element={<Layout><AllPlans /></Layout>} />
                <Route path="/shop/diet-plans" element={<Layout><AllPlans /></Layout>} />
                <Route path="/shop/all-plans" element={<Layout><AllPlans /></Layout>} />
                
                {/* Nutritionists routes */}
                <Route path="/nutritionists/find" element={<Layout><Nutritionists /></Layout>} />
                <Route path="/nutritionists/experts" element={<Layout><OurExperts /></Layout>} />
                <Route path="/become-nutritionist" element={<Layout><BecomeNutritionist /></Layout>} />
                
                {/* Auth routes */}
                <Route path="/auth/login" element={<Login />} />
                <Route path="/auth/signup" element={<SignUp />} />
                <Route path="/auth/partner-signup" element={<PartnerSignUp />} />
                <Route path="/auth/forgot-password" element={<ForgotPassword />} />
                
                {/* Dashboard redirect */}
                <Route 
                  path="/dashboard" 
                  element={
                    <ProtectedRoute 
                      isAllowed={!!currentUser}
                      redirectPath="/auth/login"
                    >
                      {currentUser?.role === 'admin' ? (
                        <Navigate to="/admin" replace />
                      ) : currentUser?.role === 'partner' ? (
                        <Navigate to="/partner" replace />
                      ) : (
                        <Navigate to="/customer" replace />
                      )}
                    </ProtectedRoute>
                  } 
                />
                
                {/* Partner routes */}
                <Route 
                  path="/partner/*" 
                  element={
                    <ProtectedRoute 
                      isAllowed={!!currentUser && currentUser.role === 'partner' && !currentUser.pendingApproval} 
                      redirectPath="/auth/login"
                    >
                      <PartnerRoutes />
                    </ProtectedRoute>
                  } 
                />
                
                {/* Customer routes */}
                <Route 
                  path="/customer/*" 
                  element={
                    <ProtectedRoute 
                      isAllowed={!!currentUser && currentUser.role === 'customer'} 
                      redirectPath="/auth/login"
                    >
                      <CustomerRoutes />
                    </ProtectedRoute>
                  } 
                />
                
                {/* Admin routes */}
                <Route 
                  path="/admin/*" 
                  element={
                    <ProtectedRoute 
                      isAllowed={!!currentUser && currentUser.role === 'admin'} 
                      redirectPath="/auth/login"
                    >
                      <AdminRoutes />
                    </ProtectedRoute>
                  } 
                />

                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </NotificationProvider>
          </MessageProvider>
        </Router>
      </ThemeProvider>
    </AuthProvider>
  );
}

export default App; 
